import { Badge, Menu } from "../utils/local-antd";
import type { MenuProps } from "../utils/local-antd";
import { TFunction } from "i18next";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { createGlobalStyle } from "styled-components";
import { Text } from "./Text";
import { Flex } from "./Flex";

import { PermissionType } from "../model/permissions/types/PermissionType";
import { User } from "../model/user/User";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import { SideBarRoutes } from "../routes/types/SideBarRoutes.enum";
import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { FontSize } from "./Text";
import { UserRoles } from "../model/user/types/UserRoles";
import { moduleActions } from "../redux/module/module.slice";
import { moduleSliceSelectors } from "../redux/module/module.selector";
import { ModuleDefinition } from "../model/module/types/module-definition/ModuleDefinition";
import { Rounded } from "../theme/Rounded";
import isEmpty from "lodash/isEmpty";
import { getFontAwesomeIcon } from "../utils/getFontAwesomeIcon";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { EntityIcons, PageIcons } from "../utils/GlobalIcons";
import { userActions } from "../redux/user/user.slice";
import { TestIDs } from "../tests/types/testIds";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons/faGoogleDrive";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { isImpersonation } from "../redux/store-utils";
import { integrationSliceSelectors } from "../redux/integration/integration.selector";
import { orgChatSliceSelectors } from "../redux/chat/orgChat.selector";
import { useTheme } from "../ThemeContext";
import { ThemeType } from "../theme/Theme";

const IMPERSONATION_BANNER_HEIGHT = "1.75rem";

const WHATSAPP_INBOX_ROUTE = "/integration/whatsapp-meta";
const GOOGLE_DRIVE_ROUTE = "/integration/google-drive";

const ThemedMenu = createGlobalStyle<{
  $isTherePSA: boolean;
  $isImpersonating: boolean;
}>`
  /* Changing the width values here requires updating:
    - The Container's 'width' and 'inset-inline-start' in DailyStatsFooter.tsx
    - The '.ant-layout-sider-trigger' class's width in Layout.tsx
    - The LogoWrapper's 'width' in Header.tsx
  */
  .ant-layout-sider {
    min-width: 190px !important;
    max-width: 190px !important;
    width: 190px !important;
  }

  .ant-layout-sider-collapsed {
    min-width: 60px !important;
    max-width: 60px !important;
    width: 60px !important;
  }

  .ant-layout-sider-collapsed .ant-menu-item {
    justify-content: center !important;
    gap: 0 !important;
  }

  .ant-menu {
    position: relative;
    /* 3.25rem  = The header's height, 6rem = The PSAUnreadBanner's height */
    top: ${({ $isTherePSA, $isImpersonating }) => `calc(3.25rem + ${$isTherePSA ? "6rem" : "0rem"} + ${$isImpersonating ? IMPERSONATION_BANNER_HEIGHT : "0rem"})`}  ;
    background-color: transparent;
    padding: 0;
    overflow-y: auto;
    height: ${({ $isTherePSA, $isImpersonating }) => `calc(100% - 3.25rem - ${$isTherePSA ? "6rem" : "0rem"} - ${$isImpersonating ? IMPERSONATION_BANNER_HEIGHT : "0rem"})`} ;
    scrollbar-width: thin; 
    scrollbar-color: ${({ theme }) => theme.sub}90 transparent;
  }

  .ant-menu::-webkit-scrollbar {
    width: 3px;
  }

  .ant-menu::-webkit-scrollbar-track {
    background: transparent;
  }

  .ant-menu::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sub}90;
    border-radius: ${Rounded.md};
  }

  .ant-menu::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.sub}; ;
  }

  .ant-menu-item {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between;
    margin: 0 !important;
    font-size: calc(${FontSize.m} - 0.05rem) !important;
    /* Changing the hight of the header requires updating the Container's 'height' in SubHeader.tsx */
    height: 3rem !important;
    width: 100% !important;
    border-radius: 0 !important; 
    color: ${({ theme }) => theme.sub} !important;
    gap: ${Spacing.m} !important;
    padding-inline-start: ${Spacing.m} !important;
  }

  .ant-menu-inline-collapsed .ant-menu-item .ant-menu-title-content {
    width: 0;
    opacity: 0;
  }

  .ant-menu-item svg {
    width: 1.5rem;
  }
`;

const getMenuItems = (config: {
  t: TFunction;
  navigate: NavigateFunction;
  collapsed: boolean;
  user: User | undefined;
  moduleDefinitions?: ModuleDefinition[];
  totalUnread: number;
  orgChatUnreadCount: number;
  theme: ThemeType;
  hasWhatsapp: boolean;
}): Required<MenuProps>["items"] => {
  const {
    t,
    navigate,
    collapsed,
    user,
    moduleDefinitions,
    totalUnread,
    orgChatUnreadCount,
    theme,
    hasWhatsapp,
  } = config;
  const permissions = user?.permissions;

  const isOrgMember = user?.roles?.includes(UserRoles.MEMBER);
  const isOrgOwner = user?.roles?.includes(UserRoles.ORGANIZATION);

  return [
    {
      key: SideBarRoutes.DASHBOARD,
      icon: <AtiraIcon icon={PageIcons.Dashboard} />,
      title: t("sideMenu.dashboard"),
      label: !collapsed ? t("sideMenu.dashboard") : null,
      onClick: () => navigate(SideBarRoutes.DASHBOARD),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.DASHBOARD),
      disabled: false,
    },
    {
      key: SideBarRoutes.FORMS,
      icon: <AtiraIcon icon={EntityIcons.Forms} />,
      title: t("sideMenu.forms"),
      label: !collapsed ? t("sideMenu.forms") : null,
      onClick: () => navigate(SideBarRoutes.FORMS),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.FORMS),
      disabled: permissions?.permissions[PermissionType.FORM].READ !== true,
    },
    {
      key: SideBarRoutes.ENTRIES,
      icon: <AtiraIcon icon={EntityIcons.Entries} />,
      title: t("sideMenu.entries"),
      label: !collapsed ? t("sideMenu.entries") : null,
      onClick: () => navigate(SideBarRoutes.ENTRIES),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.ENTRIES),
      disabled: permissions?.permissions[PermissionType.ENTRY].READ !== true,
    },
    {
      key: SideBarRoutes.DEALS,
      icon: <AtiraIcon icon={EntityIcons.Deals} />,
      title: t("sideMenu.deals"),
      label: !collapsed ? t("sideMenu.deals") : null,
      onClick: () => navigate(SideBarRoutes.DEALS),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.DEALS),
      disabled: permissions?.permissions[PermissionType.KANBAN].READ !== true,
    },
    {
      key: SideBarRoutes.REPORTS,
      icon: <AtiraIcon icon={PageIcons.Reports} />,
      title: t("sideMenu.reports"),
      label: !collapsed ? t("sideMenu.reports") : null,
      onClick: () => navigate(SideBarRoutes.REPORTS),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.REPORTS),
      disabled: isOrgMember,
    },
    {
      key: SideBarRoutes.TASKS,
      icon: <AtiraIcon icon={EntityIcons.Tasks} />,
      title: t("sideMenu.tasks"),
      label: !collapsed ? t("sideMenu.tasks") : null,
      onClick: () => navigate(SideBarRoutes.TASKS),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.TASKS),
      disabled:
        permissions?.permissions[PermissionType.LEAD_TASK].READ !== true &&
        permissions?.permissions[PermissionType.KANBAN_TASK].READ !== true &&
        permissions?.permissions[PermissionType.CONTACT_TASK].READ !== true &&
        permissions?.permissions[PermissionType.ORPHAN_TASK].READ !== true,
    },
    {
      key: SideBarRoutes.INTEGRATION,
      icon: <AtiraIcon icon={PageIcons.Integration} />,
      title: t("sideMenu.integration"),
      label: !collapsed ? (
        <Badge
          count={totalUnread}
          size="small"
          color={theme.main}
          overflowCount={99}
          offset={[10, 5]}
        >
          <Text color="white">{t("sideMenu.integration")}</Text>
        </Badge>
      ) : null,
      onClick: () => navigate(SideBarRoutes.INTEGRATION),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.INTEGRATION),
      disabled: isOrgMember,
    },
    // A member cannot connect or configure integrations, but works the WhatsApp
    // chats assigned to them - so they get the inbox itself instead, once the
    // owner has a number connected
    {
      key: WHATSAPP_INBOX_ROUTE,
      icon: <AtiraIcon icon={faWhatsapp} />,
      title: t("sideMenu.whatsapp"),
      label: !collapsed ? (
        <Badge
          count={totalUnread}
          size="small"
          color={theme.main}
          overflowCount={99}
          offset={[10, 5]}
        >
          <Text color="white">{t("sideMenu.whatsapp")}</Text>
        </Badge>
      ) : null,
      onClick: () => navigate(WHATSAPP_INBOX_ROUTE),
      "data-testid": TestIDs.general.sidemenu.link(WHATSAPP_INBOX_ROUTE),
      disabled: !isOrgMember || !hasWhatsapp,
    },
    // Same reasoning as the WhatsApp inbox above: the owner manages/uses
    // Drive from the Integrations page itself, a member can't reach that page
    // at all, so this is their only way into the file manager
    {
      key: GOOGLE_DRIVE_ROUTE,
      icon: <AtiraIcon icon={faGoogleDrive} />,
      title: t("sideMenu.googleDrive"),
      label: !collapsed ? (
        <Text color="white">{t("sideMenu.googleDrive")}</Text>
      ) : null,
      onClick: () => navigate(GOOGLE_DRIVE_ROUTE),
      "data-testid": TestIDs.general.sidemenu.link(GOOGLE_DRIVE_ROUTE),
      disabled: true,
    },
    {
      key: SideBarRoutes.CAMPAIGNS,
      icon: <AtiraIcon icon={PageIcons.Campaigns} />,
      title: t("sideMenu.campaigns"),
      label: !collapsed ? t("sideMenu.campaigns") : null,
      onClick: () => navigate(SideBarRoutes.CAMPAIGNS),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.CAMPAIGNS),
      disabled: isOrgMember,
    },
    {
      key: SideBarRoutes.TEAM_CHAT,
      icon: collapsed ? (
        // The label (and any badge wrapped around it) is zeroed out in
        // collapsed mode - see the .ant-menu-inline-collapsed override above -
        // so the badge has to live on the icon instead when collapsed.
        <Badge
          count={orgChatUnreadCount}
          size="small"
          color={theme.main}
          overflowCount={99}
          offset={[2, 4]}
        >
          <AtiraIcon icon={PageIcons.TeamChat} />
        </Badge>
      ) : (
        <AtiraIcon icon={PageIcons.TeamChat} />
      ),
      title: t("sideMenu.teamChat"),
      label: !collapsed ? (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap="xs"
          width="100%"
          minWidth="0"
        >
          <Text color="white" cutText style={{ flex: 1, minWidth: 0 }}>
            {t("sideMenu.teamChat")}
          </Text>
          {orgChatUnreadCount > 0 ? (
            <Badge
              count={orgChatUnreadCount}
              size="small"
              color={theme.main}
              overflowCount={99}
            />
          ) : null}
        </Flex>
      ) : null,
      onClick: () => navigate(SideBarRoutes.TEAM_CHAT),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.TEAM_CHAT),
      disabled: !isOrgMember && !isOrgOwner,
    },
    ...(moduleDefinitions?.map((module) => ({
      key: `/modules/${module._id}`,
      icon: <AtiraIcon icon={getFontAwesomeIcon(module.icon) || faCube} />,
      title: module.name,
      label: !collapsed ? module.name : null,
      onClick: () => navigate(`/modules/${module._id}`),
      "data-testid": TestIDs.general.sidemenu.link(SideBarRoutes.MODULES),
      disabled: false,
    })) || []),
  ].filter((item) => !item?.disabled);
};

export const SideMenu: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { theme } = useTheme();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const moduleDefinitions = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );
  const possibleLastUnreadPSAs =
    useAppSelector(userSliceSelectors.selectLastUnreadPSAs) || [];
  const totalUnread = useAppSelector(
    integrationSliceSelectors.selectTotalUnreadWhatsAppMetaChats,
  );
  const whatsappMeta = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const orgChatUnreadCount = useAppSelector(
    orgChatSliceSelectors.selectUnreadCount,
  );

  useEffect(() => {
    dispatch(moduleActions.getAllModuleDefinitions({ userId: user?._id! }));
    dispatch(userActions.getLoggedInUser({ userId: user?._id! }));
  }, [dispatch, user?._id]);

  return (
    <Fragment>
      <Menu
        mode="inline"
        theme="dark"
        items={getMenuItems({
          t,
          navigate,
          collapsed,
          user,
          moduleDefinitions,
          totalUnread,
          orgChatUnreadCount,
          theme,
          hasWhatsapp: Boolean(whatsappMeta),
        })}
        selectedKeys={[pathname]}
      />

      <ThemedMenu
        $isTherePSA={!isEmpty(possibleLastUnreadPSAs)}
        $isImpersonating={isImpersonation()}
      />
    </Fragment>
  );
};
